import { useEffect } from "react";
import { useFeedbackFlow } from "../../hooks/use-feedback-flow";
import type { ApiPage, Brandkit, FormBrand } from "../../types/api.types";

interface StepNegativeThankYouProps {
	brandkit: Brandkit;
	brand: FormBrand;
	brandLogo?: string | null;
	showBackButton?: boolean;
	onBack?: () => void;
	stepKey?: string;
	pageConfig: ApiPage;
	isDarkMode?: boolean;
}

export default function StepNegativeThankYou({
	brandkit,
	brand,
	stepKey,
	pageConfig,
	isDarkMode,
}: StepNegativeThankYouProps) {
	const { complete } = useFeedbackFlow();

	useEffect(() => {
		complete();
	}, [complete]);

	const title = pageConfig.attributes.title || "Thanks for your honesty.";
	const description =
		pageConfig.attributes.description ||
		"Your feedback will help us improve. We truly appreciate you taking the time.";
	const imageUrl =
		"image_url" in pageConfig.attributes && pageConfig.attributes.image_url
			? pageConfig.attributes.image_url
			: "/img-feedback-heart.png";
	const showBranding = brand?.branding !== "disabled";
	const brandLogo =
		brandkit?.logo_icon_with_label_url ||
		brandkit?.logo_icon_only_url ||
		brandkit?.logo_label_only_url ||
		brandkit?.logo_inverted_icon_with_label_url ||
		brandkit?.logo_inverted_icon_only_url ||
		brandkit?.logo_inverted_label_only_url ||
		null;

	return (
		<div
			className={`min-h-screen flex flex-col items-center justify-center px-6 relative overflow-hidden ${isDarkMode ? "bg-gray-950" : "bg-white"}`}
		>
			{/* Logo */}
			<div
				key={stepKey ? `logo-${stepKey}` : undefined}
				className="animate-fade-in-up mb-6"
			>
				{brandLogo ? (
					<img
						src={brandLogo}
						alt={brand.name}
						className="h-10 md:h-12 w-auto object-contain"
					/>
				) : (
					<span
						className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-t-primary"}`}
						style={{
							fontFamily: "var(--font-body)",
							fontWeight: "var(--font-body-weight)",
						}}
					>
						{brand.name}
					</span>
				)}
			</div>

			{/* Title */}
			<div
				key={stepKey ? `title-${stepKey}` : undefined}
				className="animate-fade-in-up animation-delay-100 text-center"
			>
				<h1
					className={`text-[36px] font-semibold leading-tight tracking-tight ${isDarkMode ? "text-white" : "text-t-primary"}`}
					style={{
						fontFamily: "var(--heaeding-body)",
						fontWeight: "var(--font-heading-weight)",
					}}
				>
					{title}
				</h1>
			</div>

			{/* Subtitle */}
			<div
				key={stepKey ? `subtitle-${stepKey}` : undefined}
				className="animate-fade-in-up animation-delay-200 text-center mt-3 max-w-md"
			>
				<p
					className={`text-base md:text-lg leading-relaxed ${isDarkMode ? "text-gray-300" : "text-secondary"}`}
					style={{
						fontFamily: "var(--font-body)",
						fontWeight: "var(--font-body-weight)",
					}}
				>
					{description}
				</p>
			</div>

			<div className="w-full max-w-md mx-auto group cursor-pointer mt-10">
				<img src={imageUrl} alt="" className="w-full h-auto" />
			</div>

			{/* Powered by badge */}
			{showBranding && (
				<div className="flex justify-center mt-6">
					{/* <span
						className={`text-xs sm:text-sm font-sans ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
					>
						Powered by <span className="block sm:inline"></span>{" "}
						<a
							href="https://sayabout.us/"
							target="_blank"
							rel="noopener noreferrer"
							className="underline hover:opacity-80"
						>
							Say About Us
						</a>
					</span> */}

					<a
						href="https://sayabout.us/"
						target="_blank"
						rel="noopener noreferrer"
						className="underline hover:opacity-80 block"
					>
						<img
							src="/images/badge-sayaboutus-2.svg"
							alt="build social proof with Say About Us"
							className="h-9 w-auto"
						/>
					</a>
				</div>
			)}
		</div>
	);
}
